import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ProductVariant } from "../../../../services/v2/inventory.service";
import { apiError, ui } from "../kits/shared";

/*
	Variantes de un artículo (Esteban, 2026-10-19): tallas, colores o las dos. Cada combinación color × talla es su propio
	artículo (se cuenta, se compra y se entrega por separado) y puede tener precio propio (tallas muy grandes o especiales).
	Sin <label>: el CSS global los posiciona absolutos.
*/

export interface VariantDraft { sizes: string[]; colors: string[]; existingColor: string; existingSize: string }
export const EMPTY_DRAFT: VariantDraft = { sizes: [], colors: [], existingColor: "", existingSize: "" };

/** "  azul   marino " → "Azul Marino", igual que el back. */
export const normalizeColor = (text: string): string => text.replace(/·/g, " ").replace(/\s+/g, " ").trim()
	.toLocaleLowerCase("es").replace(/(^|\s)(\S)/g, (_, space: string, letter: string) => space + letter.toLocaleUpperCase("es"))
	.replace(/ (De|Del|Y|Con|La|El|Los|Las)(?= )/g, (word) => word.toLocaleLowerCase("es"));

/** Lo que ya tiene un producto, para no ofrecerlo otra vez y saber si las variantes existentes necesitan color o talla. */
export const variantFacts = (variants: { sizeValue: string | null; colorValue?: string | null }[]) => {
	const live = variants || [];
	return {
		count: live.length,
		sizes: Array.from(new Set(live.map((variant) => variant.sizeValue).filter(Boolean) as string[])),
		colors: Array.from(new Set(live.map((variant) => variant.colorValue).filter(Boolean) as string[])),
		colorless: live.filter((variant) => !variant.colorValue).length,
		sizeless: live.filter((variant) => !variant.sizeValue).length
	};
};

/** Cuántas variantes nuevas salen: se completa la matriz color × talla con lo que ya existe. */
export const newVariantCount = (facts: ReturnType<typeof variantFacts>, draft: VariantDraft): number => {
	if (draft.colors.length < 1 && draft.sizes.length < 1) return 0;
	const lower = (list: string[]) => list.map((color) => color.toLocaleLowerCase("es"));
	const colors = new Set([...lower(facts.colors), ...lower(draft.colors), ...(draft.existingColor ? lower([draft.existingColor]) : [])]);
	const sizes = new Set([...facts.sizes, ...draft.sizes, ...(draft.existingSize ? [draft.existingSize] : [])]);
	return Math.max(0, Math.max(1, colors.size) * Math.max(1, sizes.size) - facts.count);
};

/** Falta decir de qué color/talla son las variantes que ya existían sin ese dato. */
export const draftMissing = (facts: ReturnType<typeof variantFacts>, draft: VariantDraft): string | null => {
	if (draft.colors.length > 0 && facts.count > 0 && facts.colorless > 0 && !draft.existingColor) return "Elige de qué color son las variantes que ya existen.";
	if (draft.sizes.length > 0 && facts.count > 0 && facts.sizeless > 0 && !draft.existingSize) return "Elige de qué talla son las variantes que ya existen.";
	return null;
};

/**
	Orden de una talla como en el back: las letras por cuántas X traen (XXXS antes que XS, 8XL después de 7XL, también las que
	escriben a mano), los números por su valor; lo demás, en el orden de la escala o al final.
*/
export const letterValue = (size: string): number | null => {
	const match = /^(\d*)(X*)(S|M|L)$/.exec(String(size).trim().toUpperCase());
	if (!match || (match[1] !== "" && match[2] !== "X") || (match[3] === "M" && (match[1] !== "" || match[2] !== ""))) return null;
	const xs = match[1] !== "" ? Number(match[1]) : match[2].length;
	return match[3] === "M" ? 0 : match[3] === "S" ? -1 - xs : 1 + xs;
};
export const sizeRank = (scale: string[], size: string): number => {
	const letter = letterValue(size);
	if (letter !== null) return letter;
	// Numbers by value; shoe pairs ("36/37") by their first number.
	const number = /^(\d+(?:\.\d+)?)(?:\/\d+)?$/.exec(String(size));
	if (number) return 1000 + Number(number[1]);
	const index = scale.indexOf(size);
	return index >= 0 ? 5000 + index : 9999;
};
export const sortSizes = (scale: string[], sizes: string[]): string[] => sizes.map(String).sort((a, b) => sizeRank(scale, a) - sizeRank(scale, b));

/** Igual que el back: en mayúsculas ("9XL", "32W"), sin "·" ni "|", hasta 12. Null si no sirve. */
export const cleanSize = (sizeType: string, text: string): string | null => {
	const size = text.replace(/\s+/g, " ").trim();
	if (size === "" || size.length > 12 || /[·|]/.test(size) || sizeType === "onesize") return null;
	return size.toUpperCase();
};

/** Las tallas del botón rápido: en letras XXS a 7XL (las de siempre); en las demás escalas, todas. */
export const quickSizes = (sizeType: string, scale: string[]): string[] => sizeType === "letters"
	? scale.filter((size) => { const value = letterValue(size); return value !== null && value >= -3 && value <= 8; })
	: scale;

const chip = (active: boolean, done: boolean): string => "btn btn-sm rounded-pill " + (done ? "btn-outline-success" : active ? "btn-primary" : "btn-outline-secondary");

/** Elegir tallas y colores. Lo que ya existe sale con ✓ y no se puede volver a elegir. */
export const VariantAxes: React.FC<{
	catalogs: any; sizeType: string; facts: ReturnType<typeof variantFacts>; draft: VariantDraft; busy?: boolean; onChange: (draft: VariantDraft) => void;
}> = ({ catalogs, sizeType, facts, draft, busy, onChange }) => {
	const [otherColor, setOtherColor] = useState<string>("");
	const [otherSize, setOtherSize] = useState<string>("");
	const scale = sizeType && sizeType !== "none" ? (catalogs?.sizeTypes?.[sizeType]?.values || []) : [];
	const scaleValues: string[] = scale.map((size: any) => String(size.value));
	const allowCustom = !!catalogs?.sizeTypes?.[sizeType]?.allowCustom;
	// Tallas fuera de la escala (8XL, 31…): las que ya tiene el producto y las que escribieron aquí también salen como botones.
	const extraSizes = sortSizes(scaleValues, Array.from(new Set([...facts.sizes, ...draft.sizes])).filter((size) => !scaleValues.includes(size)));
	const allSizes = sortSizes(scaleValues, [...scaleValues, ...extraSizes]);
	const labelOf = (size: string) => (scale.find((entry: any) => String(entry.value) === size) || { label: size }).label;
	const quick = quickSizes(sizeType, scaleValues).filter((size) => !facts.sizes.includes(size));
	const quickOn = quick.length > 0 && quick.every((size) => draft.sizes.includes(size));
	const toggleQuick = () => onChange({ ...draft, sizes: quickOn ? draft.sizes.filter((size) => !quick.includes(size)) : sortSizes(scaleValues, Array.from(new Set([...draft.sizes, ...quick]))) });
	const addOtherSize = () => {
		const size = cleanSize(sizeType, otherSize);
		if (size === null) return;
		const known = allSizes.find((value) => value.toUpperCase() === size.toUpperCase()) || size;
		if (!facts.sizes.includes(known) && !draft.sizes.includes(known)) onChange({ ...draft, sizes: sortSizes(scaleValues, [...draft.sizes, known]) });
		setOtherSize("");
	};
	const suggestions: string[] = catalogs?.colorSuggestions || [];
	const existingColorsLower = facts.colors.map((color) => color.toLocaleLowerCase("es"));
	const isExistingColor = (color: string) => existingColorsLower.includes(color.toLocaleLowerCase("es"));
	const hasDraftColor = (color: string) => draft.colors.some((picked) => picked.toLocaleLowerCase("es") === color.toLocaleLowerCase("es"));
	const toggleColor = (color: string) => onChange({
		...draft,
		colors: hasDraftColor(color) ? draft.colors.filter((picked) => picked.toLocaleLowerCase("es") !== color.toLocaleLowerCase("es")) : [...draft.colors, color],
		existingColor: hasDraftColor(color) && draft.existingColor.toLocaleLowerCase("es") === color.toLocaleLowerCase("es") ? "" : draft.existingColor
	});
	const addOther = () => {
		const color = normalizeColor(otherColor);
		if (color === "" || color.length > 30) return;
		if (!isExistingColor(color) && !hasDraftColor(color)) onChange({ ...draft, colors: [...draft.colors, color] });
		setOtherColor("");
	};
	// Los colores que no están en la lista sugerida (los que ya tiene o escribieron) también salen como botones.
	const extraColors = [...facts.colors, ...draft.colors].filter((color, index, list) =>
		!suggestions.some((suggestion) => suggestion.toLocaleLowerCase("es") === color.toLocaleLowerCase("es"))
		&& list.findIndex((other) => other.toLocaleLowerCase("es") === color.toLocaleLowerCase("es")) === index);
	const toCreate = newVariantCount(facts, draft);

	return <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
		{scale.length ? <div>
			<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
				<span style={ui.caption}>Tallas</span>
				{quick.length > 1 ? <button type="button" className={chip(quickOn, false)} disabled={busy} onClick={toggleQuick}>
					{sizeType === "letters" ? "XXS a 7XL" : "Todas"}</button> : null}
			</div>
			<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
				{allSizes.map((size) => {
					const done = facts.sizes.includes(size);
					const active = done || draft.sizes.includes(size);
					return <button key={size} type="button" disabled={busy || done} title={done ? "Ya existe" : undefined} className={chip(active, done)}
						onClick={() => onChange({ ...draft, sizes: draft.sizes.includes(size) ? draft.sizes.filter((value) => value !== size) : sortSizes(scaleValues, [...draft.sizes, size]),
							existingSize: draft.sizes.includes(size) && draft.existingSize === size ? "" : draft.existingSize })}>{labelOf(size)}{done ? " ✓" : ""}</button>;
				})}
			</div>
			{allowCustom ? <div style={{ display: "flex", gap: "6px", marginTop: "6px", maxWidth: "340px" }}>
				<input type="text" className="form-control" style={ui.input} value={otherSize} maxLength={12} disabled={busy} placeholder={sizeType === "letters" ? "Otra talla (ej. 9XL)" : "Otra talla"}
					onChange={(event) => setOtherSize(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addOtherSize(); } }} />
				<button type="button" className={ui.btn} disabled={busy || cleanSize(sizeType, otherSize) === null} onClick={addOtherSize}>Agregar</button>
			</div> : null}
		</div> : null}
		<div>
			<span style={ui.caption}>Colores · opcional</span>
			<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
				{[...suggestions, ...extraColors].map((color) => {
					const done = isExistingColor(color);
					const active = done || hasDraftColor(color);
					return <button key={color} type="button" disabled={busy || done} title={done ? "Ya existe" : undefined} className={chip(active, done)}
						onClick={() => toggleColor(color)}>{color}{done ? " ✓" : ""}</button>;
				})}
			</div>
			<div style={{ display: "flex", gap: "6px", marginTop: "6px", maxWidth: "340px" }}>
				<input type="text" className="form-control" style={ui.input} value={otherColor} maxLength={30} disabled={busy} placeholder="Otro color"
					onChange={(event) => setOtherColor(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addOther(); } }} />
				<button type="button" className={ui.btn} disabled={busy || normalizeColor(otherColor) === ""} onClick={addOther}>Agregar</button>
			</div>
		</div>
		{draft.colors.length > 0 && facts.count > 0 && facts.colorless > 0 ? <div style={{ maxWidth: "340px" }}>
			<span style={ui.caption}>Las {facts.colorless} que ya existen son color *</span>
			<select className="form-select" style={ui.input} value={draft.existingColor} disabled={busy} onChange={(event) => onChange({ ...draft, existingColor: event.target.value })}>
				<option value="">Elige…</option>
				{draft.colors.map((color) => <option key={color} value={color}>{color}</option>)}
			</select>
			<span style={{ ...ui.tiny, display: "block", marginTop: "3px" }}>Lo que ya está contado se queda en ellas.</span>
		</div> : null}
		{draft.sizes.length > 0 && facts.count > 0 && facts.sizeless > 0 ? <div style={{ maxWidth: "340px" }}>
			<span style={ui.caption}>Las {facts.sizeless} que ya existen son talla *</span>
			<select className="form-select" style={ui.input} value={draft.existingSize} disabled={busy} onChange={(event) => onChange({ ...draft, existingSize: event.target.value })}>
				<option value="">Elige…</option>
				{draft.sizes.map((size) => <option key={size} value={size}>{size}</option>)}
			</select>
			<span style={{ ...ui.tiny, display: "block", marginTop: "3px" }}>Lo que ya está contado se queda en ellas.</span>
		</div> : null}
		{toCreate > 0 ? <span style={ui.small}>Se {toCreate === 1 ? "crea 1 variante" : "crean " + toCreate + " variantes"}.</span> : null}
	</div>;
};

/** Ficha de variantes: precio por variante (Super Admin fija, los demás sugieren) y agregar tallas o colores. */
export const VariantesModal: React.FC<{
	campusId: number; catalogs: any; product: any; currency: string; canEdit: boolean; onChanged: (message: string) => void; onClose: () => void;
}> = ({ campusId, catalogs, product: initial, currency, canEdit, onChanged, onClose }) => {
	const http = HttpManager.getInstance();
	const isSuper = !!catalogs?.isSuperAdmin;
	const [product, setProduct] = useState<any>(initial);
	const [draft, setDraft] = useState<VariantDraft>(EMPTY_DRAFT);
	const [selected, setSelected] = useState<number[]>([]);
	const [price, setPrice] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const variants: ProductVariant[] = (product.variants || []).filter((variant: ProductVariant) => !variant.deprecatedAt);
	const facts = useMemo(() => variantFacts(variants), [product]);
	const modelPrice = product.pricePending ? null : Number(product.basePrice ?? product.price ?? 0);
	const missing = draftMissing(facts, draft);
	const toCreate = newVariantCount(facts, draft);

	useEffect(() => { setSelected((current) => current.filter((id) => variants.some((variant) => variant.id === id))); }, [product]);

	const reload = async () => {
		const response = await http.inventoryService.get(campusId, product.id);
		setProduct(response.data);
	};

	const run = async (action: () => Promise<any>, fallback: string) => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const response = await action();
			const message = response?.data?.message || "Listo";
			await reload();
			setNotice(message);
			onChanged(message);
			return true;
		} catch (requestError: any) {
			setError(apiError(requestError, fallback));
			return false;
		} finally {
			setBusy(false);
		}
	};

	const create = async () => {
		if (missing || toCreate < 1) return;
		if (await run(() => http.inventoryService.createVariants(campusId, product.id, draft.sizes, draft.colors, draft.existingColor, draft.existingSize), "No se pudieron crear las variantes.")) setDraft(EMPTY_DRAFT);
	};
	const savePrice = async (value: number | null) => {
		if (selected.length < 1) return;
		if (await run(() => http.inventoryService.variantPrices(campusId, product.id, selected, value), "No se pudo guardar el precio.")) { setSelected([]); setPrice(""); }
	};

	// Selección rápida: todas las de una talla (XXL en todos los colores) o de un color.
	const quick: { key: string; label: string; ids: number[] }[] = [
		...facts.sizes.map((size) => ({ key: "s" + size, label: size, ids: variants.filter((variant) => variant.sizeValue === size).map((variant) => variant.id) })),
		...(facts.sizes.length && facts.colors.length > 1 ? facts.colors.map((color) => ({ key: "c" + color, label: color, ids: variants.filter((variant) => variant.colorValue === color).map((variant) => variant.id) })) : [])
	];
	const allIn = (ids: number[]) => ids.length > 0 && ids.every((id) => selected.includes(id));
	const toggleIds = (ids: number[]) => setSelected(allIn(ids) ? selected.filter((id) => !ids.includes(id)) : Array.from(new Set([...selected, ...ids])));

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "720px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
					<div style={{ flex: 1, minWidth: 0 }}>
						<h4 style={{ marginBottom: "2px" }}>Variantes de {product.name}</h4>
						<div style={ui.small}>Precio del producto: {modelPrice === null ? <span style={{ color: "var(--wa-oro)", fontWeight: 600 }}>sin precio</span> : currency + modelPrice.toFixed(2)}</div>
					</div>
					<button type="button" className={ui.btn} disabled={busy} onClick={onClose}>Cerrar</button>
				</div>
				{/* Arriba, donde se ve: la tabla y la sección de agregar hacen que el modal tenga scroll. */}
				{notice ? <div className="alert alert-success" style={{ marginTop: "10px", marginBottom: 0, padding: "8px 12px", fontSize: "14px" }}>{notice}</div> : null}
				{error ? <div className="alert alert-danger" style={{ marginTop: "10px", marginBottom: 0, padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}

				{variants.length ? <>
					{quick.length > 1 && canEdit ? <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", margin: "12px 0 6px", alignItems: "center" }}>
						<span style={ui.caption}>Elegir</span>
						<button type="button" className={chip(allIn(variants.map((variant) => variant.id)), false)} disabled={busy} onClick={() => toggleIds(variants.map((variant) => variant.id))}>Todas</button>
						{quick.map((option) => <button key={option.key} type="button" className={chip(allIn(option.ids), false)} disabled={busy} onClick={() => toggleIds(option.ids)}>{option.label}</button>)}
					</div> : <div style={{ height: "10px" }} />}
					<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
						<table style={{ width: "100%", borderCollapse: "collapse" }}>
							<thead><tr>
								{canEdit ? <th style={{ ...ui.th, width: "34px" }}></th> : null}
								<th style={ui.th}>Variante</th>
								<th style={{ ...ui.th, ...ui.right }}>Existencia</th>
								<th style={{ ...ui.th, ...ui.right }}>Precio</th>
							</tr></thead>
							<tbody>{variants.map((variant) => <tr key={variant.id} style={{ cursor: canEdit ? "pointer" : "default" }} onClick={() => { if (canEdit && !busy) toggleIds([variant.id]); }}>
								{canEdit ? <td style={ui.td}><input type="checkbox" className="form-check-input" checked={selected.includes(variant.id)} readOnly style={{ margin: 0 }} /></td> : null}
								<td style={ui.td}>
									<div style={{ fontWeight: 600 }}>{variant.label}</div>
									<div style={ui.tiny}>{variant.waCode}{variant.barcode ? " · barras " + variant.barcode : ""}</div>
								</td>
								<td style={{ ...ui.td, ...ui.right }}>{variant.onHand}</td>
								<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
									{variant.pricePending && !variant.ownPrice ? <span style={ui.muted}>sin precio</span>
										: variant.ownPrice ? <b>{currency}{Number(variant.price).toFixed(2)}</b>
										: <span style={ui.muted}>{currency}{Number(variant.price).toFixed(2)}</span>}
									{variant.ownPrice ? <div style={ui.tiny}>precio propio</div> : null}
									{variant.suggestedPrice ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>sugerido {currency}{Number(variant.suggestedPrice).toFixed(2)}</div> : null}
								</td>
							</tr>)}</tbody>
						</table>
					</div>
					{canEdit && selected.length > 0 ? <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end", marginTop: "10px" }}>
						<div style={ui.field("160px")}>
							<span style={ui.caption}>{isSuper ? "Precio" : "Precio sugerido"} para {selected.length === 1 ? "1 variante" : selected.length + " variantes"}</span>
							<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={price} disabled={busy} onChange={(event) => setPrice(event.target.value)} />
						</div>
						<button type="button" className={ui.btnPrimary} style={{ height: "34px" }} disabled={busy || !(Number(price) > 0)} onClick={() => savePrice(Number(price))}>{isSuper ? "Poner Precio" : "Sugerir Precio"}</button>
						{isSuper && variants.some((variant) => selected.includes(variant.id) && variant.ownPrice)
							? <button type="button" className={ui.btn} style={{ height: "34px" }} disabled={busy} onClick={() => savePrice(null)}>Usar Precio del Producto</button> : null}
					</div> : null}
				</> : <div style={{ ...ui.small, margin: "12px 0" }}>Todavía no tiene variantes.</div>}

				{canEdit ? <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: "1px solid var(--wa-filo)" }}>
					<div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "8px" }}>Agregar Tallas o Colores</div>
					<VariantAxes catalogs={catalogs} sizeType={product.sizeType || "none"} facts={facts} draft={draft} busy={busy} onChange={setDraft} />
					<div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
						<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !!missing || toCreate < 1} title={missing || undefined} onClick={create}>
							{busy ? "Guardando…" : toCreate < 1 ? "Crear Variantes" : toCreate === 1 ? "Crear 1 Variante" : "Crear " + toCreate + " Variantes"}</button>
					</div>
				</div> : null}

			</div>
		</div>
	</div>;
};

const chipStyle: React.CSSProperties = { display: "inline-block", border: "1px solid var(--wa-filo)", background: "var(--wa-papel-2)", borderRadius: "999px", padding: "0 7px", fontSize: "11px", lineHeight: "18px", margin: "0 3px 3px 0", whiteSpace: "nowrap" };

/** Todos los colores y todas las tallas de una pieza, cada uno a la vista (Esteban, 2026-10-29: el rango "S–XXL" no se entendía). */
export const VariantChips: React.FC<{ summary: { count: number; colors: string[]; sizes: string[] } | null | undefined }> = ({ summary }) => {
	if (!summary || summary.count < 1) return null;
	const line = (caption: string, values: string[]) => values.length < 1 ? null
		: <div style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
			<span style={{ fontSize: "11px", color: "var(--wa-apagado)", minWidth: "48px" }}>{caption}</span>
			<span>{values.map((value) => <span key={value} style={chipStyle}>{value}</span>)}</span>
		</div>;
	return <div>
		{line(summary.colors.length === 1 ? "Color" : "Colores", summary.colors)}
		{line(summary.sizes.length === 1 ? "Talla" : "Tallas", summary.sizes)}
	</div>;
};

/** "Blanco, Negro · S–XXL" para enseñar en un kit lo que la pieza trae. */
export const summaryText = (summary: { count: number; colors: string[]; sizes: string[] } | null | undefined): string | null => {
	if (!summary || summary.count < 1) return null;
	const sizes = summary.sizes.length > 2 ? summary.sizes[0] + "–" + summary.sizes[summary.sizes.length - 1] : summary.sizes.join(", ");
	return [summary.colors.join(", "), sizes].filter(Boolean).join(" · ") || summary.count + " variantes";
};
